import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { toast } from "sonner";
import { CMS_PAGES } from "@/lib/content";
import { ArrowLeft, Save, Upload, FileText, ExternalLink } from "lucide-react";

export default function StaffContent() {
  const navigate = useNavigate();
  const [pageKey, setPageKey] = useState("landing");
  const page = CMS_PAGES.find((p) => p.key === pageKey);
  const [form, setForm] = useState(page.defaults);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");
  const fileRefs = useRef({});

  useEffect(() => {
    setLoaded(false);
    api.get(`/content/${pageKey}`).then(({ data }) => {
      const clean = Object.fromEntries(Object.entries(data || {}).filter(([, v]) => v !== "" && v != null));
      setForm({ ...page.defaults, ...clean });
    }).catch(() => setForm(page.defaults)).finally(() => setLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageKey]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try { await api.put(`/content/${pageKey}`, { items: form }); toast.success(`${page.label} content saved`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save content"); }
    finally { setSaving(false); }
  };

  const uploadImage = async (key, file) => {
    if (!file) return;
    setUploading(key);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/content/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set(key, data.url);
      toast.success("Image uploaded — remember to Save");
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(""); }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <Navbar />
      <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
        <button onClick={() => navigate("/staff")} className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-slate-500 dark:text-slate-400 hover:text-slate-900" data-testid="content-back-btn"><ArrowLeft className="h-4 w-4" /> Pipeline</button>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-rose-50 text-rose-600"><FileText className="h-5 w-5" strokeWidth={2.5} /></div>
            <div>
              <h1 className="font-display text-3xl font-black tracking-tighter text-slate-950 dark:text-slate-50">Site content</h1>
              <p className="text-sm text-slate-600 dark:text-slate-300">Edit page copy, images, and search metadata. Changes go live on save.</p>
            </div>
          </div>
          <a href="/" target="_blank" rel="noreferrer" data-testid="content-preview-link" className="inline-flex items-center gap-1.5 text-sm font-semibold text-rose-600 hover:text-rose-700">Preview site <ExternalLink className="h-4 w-4" /></a>
        </div>

        <div className="mt-6 flex flex-wrap gap-2" data-testid="content-page-tabs">
          {CMS_PAGES.map((p) => (
            <button key={p.key} onClick={() => setPageKey(p.key)} data-testid={`content-tab-${p.key}`}
              className={`rounded-full border px-4 py-1.5 text-sm font-bold transition-colors ${pageKey === p.key ? "border-rose-600 bg-rose-600 text-white" : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 hover:border-slate-300"}`}>
              {p.label}
            </button>
          ))}
        </div>

        {!loaded ? (
          <p className="mt-10 text-slate-400">Loading…</p>
        ) : (
          <div className="mt-6 space-y-6">
            {page.sections.map((section) => (
              <div key={section.title} className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6">
                <h3 className="mb-4 font-display text-lg font-bold text-slate-900 dark:text-slate-100">{section.title}</h3>
                <div className="space-y-4">
                  {section.fields.map((fld) => (
                    <div key={fld.key}>
                      <Label className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">{fld.label}</Label>
                      {fld.type === "image" ? (
                        <div className="mt-1.5">
                          {form[fld.key] && <img src={form[fld.key]} alt="preview" className="mb-2 h-32 w-full rounded-md border border-slate-200 dark:border-slate-800 object-cover" data-testid={`content-img-preview-${fld.key}`} />}
                          <div className="flex flex-wrap gap-2">
                            <input ref={(el) => (fileRefs.current[fld.key] = el)} type="file" accept="image/*" className="hidden" data-testid={`content-file-${fld.key}`} onChange={(e) => uploadImage(fld.key, e.target.files?.[0])} />
                            <Button type="button" variant="outline" onClick={() => fileRefs.current[fld.key]?.click()} disabled={uploading === fld.key} data-testid={`content-upload-${fld.key}`} className="rounded-md font-semibold"><Upload className="mr-1.5 h-4 w-4" />{uploading === fld.key ? "Uploading…" : "Upload image"}</Button>
                          </div>
                          <Input value={form[fld.key] || ""} onChange={(e) => set(fld.key, e.target.value)} placeholder="…or paste an image URL" data-testid={`content-imgurl-${fld.key}`} className="mt-2" />
                        </div>
                      ) : fld.type === "textarea" ? (
                        <Textarea value={form[fld.key] || ""} onChange={(e) => set(fld.key, e.target.value)} rows={fld.rows || 3} data-testid={`content-input-${fld.key}`} className="mt-1.5" />
                      ) : fld.type === "select" ? (
                        <select
                          value={form[fld.key] || ""}
                          onChange={(e) => set(fld.key, e.target.value)}
                          data-testid={`content-input-${fld.key}`}
                          className="mt-1.5 flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm font-medium text-slate-900 outline-none transition-colors focus:border-rose-500 focus:ring-2 focus:ring-rose-100 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-100 dark:focus:ring-rose-950"
                        >
                          {fld.options.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <Input value={form[fld.key] || ""} onChange={(e) => set(fld.key, e.target.value)} data-testid={`content-input-${fld.key}`} className="mt-1.5" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <div className="flex justify-end border-t border-slate-200 pt-6 dark:border-slate-800">
              <Button onClick={save} disabled={saving} data-testid="content-save-btn" className="rounded-md bg-rose-600 px-6 font-bold text-white shadow-lg hover:bg-rose-700"><Save className="mr-1.5 h-4 w-4" />{saving ? "Saving…" : "Save changes"}</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
